import React from "react";
import { toast } from "react-toastify";
import NowTable from "../../../plan/tasks_hub/NowTable";
import TaskNameCardEmployeeModal from "../../../plan/plan_contents/TaskNameCardEmployeeModal";
import LoadSpinner from "../../../../../utilities/LoadSpinner";
import { mergeNowRowOverOccurrence } from "../../../plan/tasks_hub/taskHubUtils";
import { getRightworkAPI } from "../../utilities/requests";
import {
  API_get_now_tasks,
  API_get_plan_tasks_for_user_and_date,
} from "../../../../../constants";

// The employee's Now: the same list managers see, filtered by the backend to
// what this person is eligible for (roster + role, or assigned to them), with
// one action per row. The checklist itself opens in the existing task modal.
export default function TasksDisplayEmployee({
  storeId,
  homeCompany,
  roleNames,
  occurrences,
  save_plan_task_assignment,
  refreshOccurrences,
}) {
  const [data, setData] = React.useState(null);
  // A failed request is its own state: handing NowTable null would tell the
  // person they have nothing to do today.
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [showDone, setShowDone] = React.useState(false);
  const [showUpcoming, setShowUpcoming] = React.useState(false);
  const [workRow, setWorkRow] = React.useState(null);
  // Which tab the checklist modal opens on (the comment icon asks for Comments).
  const [workTab, setWorkTab] = React.useState("checklist");

  // silent = refresh the list in the background without the spinner; only the
  // first load and a store switch show one. A response for a store or done
  // filter the user has since moved away from is dropped, so a slow request
  // for the previous store can't overwrite the current one's rows.
  const contextRef = React.useRef(null);
  const fetchNow = React.useCallback(async (silent = false) => {
    const context = `${storeId}:${showDone}:${showUpcoming}`;
    contextRef.current = context;
    if (!silent) setLoading(true);
    try {
      const res = await getRightworkAPI(API_get_now_tasks, {
        store_id: storeId,
        include_done: showDone ? "true" : "false",
        include_upcoming: showUpcoming ? "true" : "false",
      });
      if (contextRef.current !== context) return;
      // A failed fetch clears the rows rather than leaving the previous
      // store's tasks on screen under the newly selected one.
      setData(res.status === 200 ? res.data?.redux?.payload || null : null);
      setError(res.status !== 200);
    } catch (e) {
      // A rejection is a failure like any other: without this the tab clears
      // its rows and then reports an outage as "nothing to do today".
      console.error("Error fetching tasks:", e);
      if (contextRef.current === context) {
        setData(null);
        setError(true);
      }
    } finally {
      if (!silent && contextRef.current === context) setLoading(false);
    }
  }, [storeId, showDone, showUpcoming]);

  // The spinner is for the first load and a store switch; flipping a Show
  // toggle refetches silently, keeping the rows on screen meanwhile.
  const fetchedStoreRef = React.useRef(null);
  React.useEffect(() => {
    if (storeId == null) return;
    const silent = fetchedStoreRef.current === storeId;
    fetchedStoreRef.current = storeId;
    fetchNow(silent);
  }, [fetchNow, storeId]);

  // The My Day fetch refreshes the occurrence list after every save; keep the
  // Now list in step with it.
  const occurrencesRef = React.useRef(occurrences);
  React.useEffect(() => {
    if (occurrencesRef.current !== occurrences && occurrencesRef.current !== undefined) {
      fetchNow(true);
    }
    occurrencesRef.current = occurrences;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrences]);

  const occurrenceFor = (row) =>
    (occurrences || []).find((o) => o.uuid === row.plan_task_assignment_uuid) || null;

  // The day payload is today's only; Now can carry yesterday's started work
  // and open one-offs from earlier dates. Fetch those for their own date.
  const fetchOccurrence = async (row) => {
    const res = await getRightworkAPI(API_get_plan_tasks_for_user_and_date, {
      store_id: storeId,
      specific_date: row.date,
    });
    if (res.status !== 200) return null;
    return (
      (res.data?.redux?.payload?.plan_task_assignments || []).find(
        (o) => o.uuid === row.plan_task_assignment_uuid
      ) || null
    );
  };

  // The detail fetch for a carried routine or old one-off is its own request:
  // a store switch while it is in flight must not open the previous store's
  // task against the new store. Track the store the fetch was made for and
  // close whatever checklist is open when the store changes.
  const storeRef = React.useRef(storeId);
  React.useEffect(() => {
    storeRef.current = storeId;
    setWorkRow(null);
    // Drop the previous store's rows before the new ones arrive.
    setData(null);
  }, [storeId]);

  const openWork = async (row, tab) => {
    const requestedStore = storeId;
    setWorkTab(tab === "conversation" ? "comments" : "checklist");
    let occ = occurrenceFor(row);
    if (!occ) occ = await fetchOccurrence(row);
    if (storeRef.current !== requestedStore) return;
    if (occ) {
      setWorkRow(mergeNowRowOverOccurrence(row, occ));
    } else {
      toast.error("That task could not be loaded. Refresh and try again.", { autoClose: 3000 });
    }
  };

  return (
    <div className="mt-8 px-6 pb-16">
      {loading ? (
        <div className="flex justify-center py-24">
          <LoadSpinner />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <p className="text-sm text-slate-500">Your tasks could not be loaded.</p>
          <button
            onClick={() => {
              setError(false);
              fetchNow();
            }}
            className="text-sm font-semibold text-violet-700 hover:text-violet-800"
          >
            Try again
          </button>
        </div>
      ) : (
        <NowTable
          data={data}
          storeId={storeId}
          showDone={showDone}
          showUpcoming={showUpcoming}
          onToggleUpcoming={setShowUpcoming}
          onToggleDone={setShowDone}
          onOpenTask={openWork}
          showHistory={false}
        />
      )}
      {workRow && (
        <TaskNameCardEmployeeModal
          open={!!workRow}
          handleClose={() => setWorkRow(null)}
          task={workRow}
          initialTab={workTab}
          onCommented={() => {
            fetchNow(true);
            refreshOccurrences?.();
          }}
          isAssignedToEveryone={!workRow.is_pinned}
          save_plan_task_assignment={save_plan_task_assignment}
          homeCompany={homeCompany}
          roleNames={roleNames}
          setShowLightbox={() => {}}
          setSelectedImage={() => {}}
        />
      )}
    </div>
  );
}
